import {
  PulseStatusProgress,
  v2Endpoint,
  PulseXhrRequestStatus,
} from '@pcs/pulse-components';
import PulseBryntumGrid from '@pcs/pulse-components/PulseBryntumGrid';
import '@pcs/pulse-components/PulseBryntumGrid/PulseBryntumGrid.css';
import clsx from 'clsx';
import utilities, {
  camelizeKeys,
  dateFormatBasedOnCountry,
  parseJsonApi,
  useInterval,
} from 'jsAssets/utilities';
import { getOrCreateEcho, isEchoConnected } from 'jsAssets/websocket/echo';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import * as PropTypes from 'prop-types';
import qs from 'qs';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import PulseAssetDetail from '../../../asset-detail';
import { useReviewIdByDeepLinks } from '../../../asset-detail/helpers';
import {
  COLUMNS_CONFIGS_KEY_INSIDE_PROJECT,
  COLUMNS_CONFIGS_KEY_NON_PROJECT,
  COLUMNS_CONFIGS_KEY_TASK_FORM,
  FIELDS_TO_COLUMNS,
  REVIEW_GRID_JQ,
} from '../../constants/review-grid-constant';
import {
  CONFIG,
  GRID_ACTIONS,
  REQUEST,
  URLS,
  USER,
  defaultV3Image,
} from '../../helper';
import ReviewModalContext from '../ReviewModalContext';
import EmptyReview from './components/empty-review';
import {
  DateCreatedRenderer,
  DeadlineRenderer,
  ProgressRenderer,
  ProjectRenderer,
  ReviewRenderer,
  ReviewerRenderer,
  SourceRenderer,
  StatusRenderer,
  ViewBtnRenderer,
} from './review-grid-column-renderer';
import styles from './styles.modules.less';
import { STAGE_STATUS } from '../../constants/review-stages';
import { DEFAULT_PROGRESS } from '../ReviewStagesDetail';

const REVIEW_URL = `${URLS.app}api/reviews`;

const CONTENT = REQUEST.content || '';

const RUN_WEBSOCKETS = CONFIG.ws.websocket_enabled;
const defaultTime = Number(CONFIG?.ws?.default_polling_time ?? 15000);

if (RUN_WEBSOCKETS) {
  getOrCreateEcho();
}

const UNAVAILABLE_REVIEW_STATUSES = ['failed', 'processing', 'uploading'];

const DEFAULT_AJAX_STORE_PARAMS = {
  jq: REVIEW_GRID_JQ,
};

const DEFAULT_COLUMNS_NON_PROJECT = [
  {
    field: 'source_img',
    cellCls: 'source-img-col',
    text: '',
    width: 60,
    minWidth: 65,
    align: 'center',
    hideable: false,
    sortable: false,
    draggable: false,
    resizable: false,
    renderer: ({ record }) => {
      const { data } = record || {};
      return <SourceRenderer record={data} />;
    },
  },
  {
    field: 'source',
    text: 'Review Title',
    width: 200,
    draggable: false,
    cellCls: styles['custom-col-review'],
    renderer: ({ record }) => {
      const { data } = record || {};
      return <ReviewRenderer record={data?.round_title} />;
    },
  },
  {
    field: 'round_number',
    text: 'Round',
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    align: 'center',
    hidden: true,
    draggable: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'job_number',
    text: 'Job Number',
    cellCls: clsx(styles['custom-col-project'], 'job-number-col'),
    width: 150,
    draggable: false,
  },
  {
    field: 'job_title',
    text: 'Project',
    cellCls: styles['custom-col-project'],
    width: 150,
    draggable: false,
    renderer: ({ value }) => {
      return <ProjectRenderer record={value} />;
    },
  },
  {
    field: 'name',
    text: 'Latest Stage',
    cellCls: styles['custom-col-wrapper'],
    width: 150,
    draggable: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'brand_title',
    text: 'Brand',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    renderer: ({ value }) => {
      return <span className={styles['review-col-ellipsis']}>{value}</span>;
    },
  },
  {
    field: 'round_type_order_by',
    text: 'Type',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    renderer: ({ value }) => {
      return (
        <span
          className={clsx(
            styles['review-col-uppercase'],
            styles['review-col-ellipsis']
          )}
        >
          {value}
        </span>
      );
    },
  },
  {
    field: 'members',
    text: 'Reviewer(s)',
    cellCls: 'reviewer-col',
    draggable: false,
    hidden: true,
    renderer: ({ record, column }) => {
      return (
        <ReviewerRenderer record={record.data} widthColumn={column?.width} />
      );
    },
  },
  {
    field: 'deadline',
    text: 'Deadline',
    type: 'date',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    htmlEncode: false,
    renderer: ({ record }) => {
      return <DeadlineRenderer record={record.data} />;
    },
  },
  {
    field: 'progress',
    text: 'Progress',
    draggable: false,
    hidden: true,
    htmlEncode: false,
    sortable: false,
    width: 108,
    minWidth: 108,
    renderer: ({ record }) => {
      return <ProgressRenderer record={record.data} />;
    },
  },
  {
    field: 'comment_count',
    text: 'Comments',
    draggable: false,
    hidden: true,
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    htmlEncode: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'status_order_by',
    text: 'Round Status',
    width: 175,
    draggable: false,
    renderer: ({ record }) => {
      return <StatusRenderer record={record.data} />;
    },
  },
  {
    field: 'created_at',
    text: 'Date Created',
    align: 'left',
    hidden: false,
    width: 150,
    draggable: false,
    renderer: ({ record }) => {
      return <DateCreatedRenderer record={record.data} />;
    },
  },
];

const DEFAULT_COLUMNS_INSIDE_PROJECT = [
  {
    field: 'source_img',
    cellCls: 'source-img-col',
    text: '',
    width: 60,
    minWidth: 65,
    align: 'center',
    hideable: false,
    sortable: false,
    draggable: false,
    resizable: false,
    renderer: ({ record }) => {
      const { data } = record || {};
      return <SourceRenderer record={data} />;
    },
  },
  {
    field: 'source',
    text: 'Review Title',
    width: 200,
    draggable: false,
    cellCls: styles['custom-col-review'],
    renderer: ({ record }) => {
      const { data } = record || {};
      return <ReviewRenderer record={data?.round_title} />;
    },
  },
  {
    field: 'round_number',
    text: 'Round',
    hidden: true,
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    align: 'center',
    draggable: false,
    renderer: ({ value }) => {
      return <span className={styles['review-cell-text']}>{value}</span>;
    },
  },
  {
    field: 'name',
    text: 'Latest Stage',
    cellCls: styles['custom-col-wrapper'],
    width: 150,
    draggable: false,
    renderer: ({ value }) => {
      return <span className={styles['review-cell-text']}>{value}</span>;
    },
  },
  {
    field: 'round_type_order_by',
    text: 'Type',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    renderer: ({ value }) => {
      return (
        <span
          className={clsx(
            styles['review-col-uppercase'],
            styles['review-col-ellipsis']
          )}
        >
          {value}
        </span>
      );
    },
  },
  {
    field: 'members',
    text: 'Reviewer(s)',
    cellCls: 'reviewer-col',
    hidden: true,
    draggable: false,
    renderer: ({ record, column }) => {
      return (
        <ReviewerRenderer record={record.data} widthColumn={column?.width} />
      );
    },
  },
  {
    field: 'deadline',
    text: 'Deadline',
    type: 'date',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    htmlEncode: false,
    renderer: ({ record }) => {
      return <DeadlineRenderer record={record.data} />;
    },
  },
  {
    field: 'progress',
    text: 'Progress',
    draggable: false,
    htmlEncode: false,
    sortable: false,
    width: 108,
    minWidth: 108,
    renderer: ({ record }) => {
      return <ProgressRenderer record={record.data} />;
    },
  },
  {
    field: 'comment_count',
    text: 'Comments',
    draggable: false,
    hidden: true,
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    htmlEncode: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'status_order_by',
    text: 'Round Status',
    width: 175,
    draggable: false,
    renderer: ({ record }) => {
      return <StatusRenderer record={record.data} />;
    },
  },
  {
    field: 'created_at',
    text: 'Date Created',
    align: 'left',
    hidden: false,
    width: 150,
    draggable: false,
    renderer: ({ record }) => {
      return <DateCreatedRenderer record={record.data} />;
    },
  },
];

const DEFAULT_COLUMNS_TASK_FORM = [
  {
    field: 'source_img',
    cellCls: 'source-img-col',
    text: '',
    width: 60,
    minWidth: 65,
    align: 'center',
    hideable: false,
    sortable: false,
    draggable: false,
    resizable: false,
    renderer: ({ record }) => {
      const { data } = record || {};
      return <SourceRenderer record={data} />;
    },
  },
  {
    field: 'source',
    text: 'Review Title',
    width: 200,
    draggable: false,
    cellCls: styles['custom-col-review'],
    renderer: ({ record }) => {
      const { data } = record || {};
      return <ReviewRenderer record={data?.round_title} />;
    },
  },
  {
    field: 'round_number',
    text: 'Round',
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    align: 'center',
    draggable: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'name',
    text: 'Latest Stage',
    cellCls: styles['custom-col-wrapper'],
    width: 150,
    draggable: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'round_type_order_by',
    text: 'Type',
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    renderer: ({ value }) => {
      return (
        <span
          className={clsx(
            styles['review-col-uppercase'],
            styles['review-col-ellipsis']
          )}
        >
          {value}
        </span>
      );
    },
  },
  {
    field: 'members',
    text: 'Reviewer(s)',
    cellCls: 'reviewer-col',
    draggable: false,
    renderer: ({ record, column }) => {
      return (
        <ReviewerRenderer record={record.data} widthColumn={column?.width} />
      );
    },
  },
  {
    field: 'deadline',
    text: 'Deadline',
    type: 'date',
    hidden: true,
    cellCls: styles['custom-col-wrapper'],
    draggable: false,
    htmlEncode: false,
    renderer: ({ record }) => {
      return <DeadlineRenderer record={record.data} />;
    },
  },
  {
    field: 'progress',
    text: 'Progress',
    hidden: true,
    draggable: false,
    htmlEncode: false,
    sortable: false,
    width: 108,
    minWidth: 108,
    renderer: ({ record }) => {
      return <ProgressRenderer record={record.data} />;
    },
  },
  {
    field: 'comment_count',
    text: 'Comments',
    draggable: false,
    hidden: true,
    cellCls: clsx(styles['custom-col-wrapper'], styles['custom-col-align']),
    htmlEncode: false,
    renderer: ({ value }) => {
      return <span>{value}</span>;
    },
  },
  {
    field: 'status_order_by',
    text: 'Round Status',
    width: 175,
    draggable: false,
    renderer: ({ record }) => {
      return <StatusRenderer record={record.data} />;
    },
  },
  {
    field: 'created_at',
    text: 'Date Created',
    align: 'left',
    hidden: false,
    width: 150,
    draggable: false,
    renderer: ({ record }) => {
      return <DateCreatedRenderer record={record.data} />;
    },
  },
];

const PARAMS_DEFAULT = {
  filter: {
    job_id: REQUEST.jobId > 0 && !REQUEST.taskId ? REQUEST.jobId : undefined,
  },
};

const INSIDE_PROJECT = !!REQUEST.jobId;

const DocumentReviewGrid = (props) => {
  const {
    gridProps,
    paramFilter,
    leftNavParam,
    filterFlyoutParam,
    searchValue,
    limit = 10,
    isTaskForm,
    ticketId = 0,
    rejectFilterJobId = false,
    emptyReviewScope = 'widget',
  } = props;

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const params = useMemo(
    () => ({
      filter: {
        ...PARAMS_DEFAULT.filter,
        ...(rejectFilterJobId && { job_id: undefined }),
      },
    }),
    [rejectFilterJobId]
  );

  const queryParamsString = qs.stringify(params, { encode: false });

  const urlEndpoint = `${URLS.app}api/round-stages?${queryParamsString}`;

  const viewBtnColumn = {
    field: 'view_btn',
    region: 'right',
    text: '',
    align: 'center',
    hideable: false,
    sortable: false,
    draggable: false,
    resizable: false,
    minWidth: 165,
    cellCls: 'view-btn',
    renderer: ({ record, grid }) => {
      const currentRow = grid?.store?.records.filter((row) => {
        return row.data.round_id === record.round_id;
      });
      const currentStatus = currentRow.length
        ? currentRow[0].data.round_status
        : record.data.round_status;
      const disableViewProof =
        UNAVAILABLE_REVIEW_STATUSES.includes(currentStatus);
      return (
        <ViewBtnRenderer
          clientId={record.data?.job_office_id}
          roundNumber={record.data?.round_number}
          record={record.data}
          ajaxReviewGridStore={grid?.store}
          disableViewProofBtn={disableViewProof}
          ticketId={ticketId}
          roundId={record.data?.round_id}
        />
      );
    },
  };

  const gridRef = useRef();
  const intervalRef = useRef(0);
  const filterCondition =
    paramFilter || (isEmpty(leftNavParam) ? filterFlyoutParam : leftNavParam);
  const gridAction =
    !isEmpty(filterCondition) || searchValue
      ? GRID_ACTIONS.FILTER
      : GRID_ACTIONS.READ;
  const [isPolling, setIsPolling] = useState(false);
  const [hasReview, setHasReview] = useState(true);
  const reviewScope = isTaskForm ? 'task' : emptyReviewScope;
  const isEmptyStateReviews = !hasReview;
  let stageData;

  /**
   *
   * Handle Deepslink
   */
  const renderReviewDetail = (assetId = 0, roundId) => {
    const reviewGridAssetDetailContainer =
      document.querySelector('#review-detail');
    const reviewGridAssetDetailRoot =
      reviewGridAssetDetailContainer &&
      createRoot(reviewGridAssetDetailContainer);
    if (reviewGridAssetDetailRoot) {
      reviewGridAssetDetailRoot.render(
        <Suspense fallback={<div>Loading...</div>}>
          <PulseAssetDetail
            initialState={{
              assetDetail: {
                asset: {
                  assetId,
                },
                isZiflow: true,
                isOpenDrawer: true,
                isOpenHistory: false,
                isAdvancedPanelCreated: false,
                isOpenAdvanceDetails: false,
                isOpenWorkFlowTabs: false,
                isOpenAssetDetailsTabs: false,
                isConfirmationModalVisible: false,
              },
              assetComments: {
                comments: [],
                versionComments: [],
                ziflowActivities: [],
              },
              assetVersions: {
                comments: [],
              },
              ticket: {
                isUpdatingTicketProperty: [],
                isUpdatingTicketField: [],
              },
              ticketHistory: {},
              emailHistory: {
                data: [],
              },
              review: {
                reviewRound: null,
                tasks: [],
                isLoadingReviewRound: false,
                setOpenReviewModal,
              },
            }}
            ticketId={ticketId}
            roundId={roundId}
            assetId={assetId}
          />
        </Suspense>,
        reviewGridAssetDetailContainer
      );
    }
  };
  useEffect(() => {
    const { hash } = window.location;

    if (hash && CONTENT === 'passport-reviews') {
      handleRoundDeepLink(hash);
    }
  }, [setOpenReviewModal]);

  //
  const handleRoundDeepLink = async (hash) => {
    const [reviewIdDeepLink, roundNumberDeepLink] =
      useReviewIdByDeepLinks(hash);
    if (reviewIdDeepLink > 0) {
      utilities.blockPage();
      await v2Endpoint
        .get(`${REVIEW_URL}/${reviewIdDeepLink}`)
        .then(({ data }) => {
          const dataRes = parseJsonApi(data);
          const { rounds } = camelizeKeys(dataRes);
          if (Array.isArray(rounds)) {
            const latestRoundId = rounds[rounds.length - 1].id;
            const latestRoundNumber = rounds[rounds.length - 1].roundNumber;
            if (roundNumberDeepLink <= 0) {
              renderReviewDetail(0, rounds[rounds.length - 1].id);
              utilities.unblockPage();
              return;
            }
            rounds.forEach((round) => {
              if (round.roundNumber === roundNumberDeepLink) {
                if (latestRoundNumber > roundNumberDeepLink) {
                  window.history.pushState(
                    {
                      roundId: round.id,
                      lastRoundId: latestRoundId,
                      redirectRound: true,
                    },
                    ''
                  );
                } else {
                  window.history.pushState(
                    {
                      roundId: round.id,
                    },
                    ''
                  );
                }
                renderReviewDetail(0, round.id);
                utilities.unblockPage();
              }
            });
          }
        });
    }
  };

  /**
   * end
   * */

  useEffect(() => {
    const handleReloadGrid = (e) => {
      if (e.detail) {
        gridRef.current?.gridInstance?.store?.loadPage(1);
      }
    };
    document.addEventListener('reload-review-grid', handleReloadGrid);
    return () => {
      document.removeEventListener('reload-review-grid', handleReloadGrid);
    };
  }, []);

  useEffect(() => {
    const gridInstance = gridRef.current?.gridInstance;
    if (!gridInstance) {
      return;
    }
    const { store: gridAjaxStore } = gridInstance;

    const searchParam = {};
    if (searchValue) {
      searchParam['filter[qsearch]'] = searchValue;
    }
    const includedParam = {
      ...DEFAULT_AJAX_STORE_PARAMS,
      ...filterCondition,
      ...searchParam,
    };
    gridAjaxStore.setConfig({ params: includedParam });
    gridAjaxStore.loadPage(1, includedParam);
  }, [filterCondition, searchValue]);

  useEffect(() => {
    if (!gridRef.current) {
      return;
    }
    const { gridInstance } = gridRef.current;

    if (Array.isArray(gridRef.current.records) && handleExistReviews) {
      if (gridRef.current.records.length <= 0) {
        handleExistReviews(false);
      }
    }

    /**
     * Save the columns that should be display
     */
    gridInstance.columns.on('change', () => {
      const columns = gridInstance.columns.records.reduce(
        (results, column) => ({
          ...results,
          [column.field]: {
            width: column.width,
            hidden: column.hidden,
            order: column.parentIndex,
          },
        }),
        {}
      );

      if (INSIDE_PROJECT) {
        if (isTaskForm) {
          localStorage.setItem(
            COLUMNS_CONFIGS_KEY_TASK_FORM,
            JSON.stringify(columns)
          );
        } else {
          localStorage.setItem(
            COLUMNS_CONFIGS_KEY_INSIDE_PROJECT,
            JSON.stringify(columns)
          );
        }
      } else {
        localStorage.setItem(
          COLUMNS_CONFIGS_KEY_NON_PROJECT,
          JSON.stringify(columns)
        );
      }
    });
  }, [gridRef?.current]);

  intervalRef.current = useInterval(() => {
    if (RUN_WEBSOCKETS) {
      const WEBSOCKET_CONNECTION = isEchoConnected();

      if (WEBSOCKET_CONNECTION) {
        setIsPolling(false);
      } else {
        setIsPolling(true);
      }
    }
  }, defaultTime);

  // Connect to websocket
  useEffect(() => {
    const isRunningWebsocket = USER && RUN_WEBSOCKETS && !isPolling;
    if (isRunningWebsocket) {
      window.Echo.channel('ziflow-review-grid').listen(
        '.ziflow-event-grid',
        (response) => {
          // Need to show on console to check websocket works

          // Removing update_at field to avoid rerendering
          delete response.data.updated_at;

          if (!isEqual(response.data, stageData) && gridRef.current) {
            const gridInstance = gridRef.current.instance;
            const { store: gridAjaxStore } = gridInstance;
            const dataInCurrentViewGrid = gridAjaxStore.records;

            const updatedRow = dataInCurrentViewGrid.find((row) => {
              if (row.data.related_rounds[0].id === response.data.round_id) {
                return row;
              }
              return undefined;
            });

            if (
              updatedRow &&
              response.data.status !== STAGE_STATUS.notStarted // TODO: we just update started stage
            ) {
              const updatedData = {
                name: response.data.name,
                reviewers: response.data.reviewers,
                progress: response.data.progress,
                source: response.data.round_sources[0],
                round_number: response.data.round_name,
                round_id: response.data.round_id,
                deadline: response.data.deadline,
                round_type_order_by: response.data.round_type,
                status_order_by: response.data.round_status,
                comment_count: response.data.comment_count,
                job_id: response.data.job_id,
                job_title: response.data.job_title,
                brand_title: response.data.job_brand,
                job_number: response.data.job_number,
                created_at: response.data.created_at,
                last_round_number: response.data.last_round_number,
                manage: response.data.manage,
                round_title: response.data.round_title,
                round_status: response.data.round_status,
                round_stage_id: response.data.round_stage_id,
                round_thumbnail_link: response.data.round_thumbnail_link,
                review_id: response.data.review_id,
                decision_status_label:
                  response.data.round_decision_status_label,
              };

              updatedRow.set(updatedData);
            }
          }
          stageData = response.data;
        }
      );
    }

    let id;
    if (isPolling && isVisible) {
      id = useInterval(() => {
        const gridInstance = gridRef.current?.gridInstance;
        if (!gridInstance) {
          return;
        }
        const { store: gridAjaxStore } = gridInstance;
        const { _widgetMap } = gridInstance;
        // eslint-disable-next-line no-underscore-dangle
        const currentPage = _widgetMap.pageNumber._value;
        gridAjaxStore.loadPage(currentPage);
      }, defaultTime);
    }

    return () => {
      clearInterval(id);
      if (window.Echo) {
        window.Echo.leaveChannel('ziflow-review-grid');
      }
    };
  }, [isPolling, isVisible]);

  const handleListenerEventUpdateRoundStages = (e) => {
    const newRound = e.detail;
    if (newRound) {
      const gridInstance = gridRef.current.instance;
      const { store: gridAjaxStore } = gridInstance;
      const dataInCurrentViewGrid = gridAjaxStore.records;

      const updatedRow = dataInCurrentViewGrid.find(
        (row) => get(row, 'data.related_rounds[0].id') === newRound.roundId
      );

      if (updatedRow) {
        const updatedData = {
          round_title: newRound.title,
          round_type_order_by: newRound.roundType,
        };
        updatedRow.set(updatedData);
      }
    }
  };

  useEffect(() => {
    document.addEventListener(
      'update-review-round',
      handleListenerEventUpdateRoundStages
    );
    return () => {
      document.removeEventListener(
        'update-review-round',
        handleListenerEventUpdateRoundStages,
        false
      );
    };
  }, []);

  const columnsConfig = () => {
    let defaultColumns = COLUMNS_CONFIGS_KEY_NON_PROJECT;

    let columns;

    if (INSIDE_PROJECT) {
      if (isTaskForm) {
        columns = DEFAULT_COLUMNS_TASK_FORM;
        defaultColumns = COLUMNS_CONFIGS_KEY_TASK_FORM;
      } else {
        columns = DEFAULT_COLUMNS_INSIDE_PROJECT;
        defaultColumns = COLUMNS_CONFIGS_KEY_INSIDE_PROJECT;
      }
    } else {
      columns = DEFAULT_COLUMNS_NON_PROJECT;
    }

    const columnsConfigsStorage = localStorage.getItem(defaultColumns);

    if (columnsConfigsStorage) {
      columns = columns.map((column) => {
        const columnConfigs = JSON.parse(columnsConfigsStorage)[column.field];
        return {
          ...column,
          ...columnConfigs,
        };
      });

      columns.sort();
    }
    return [...columns, viewBtnColumn];
  };

  const AJAX_STORE_CONFIG = {
    readUrl: urlEndpoint,
    autoLoad: false,
    params: DEFAULT_AJAX_STORE_PARAMS,
    pageSize: limit,
    pageParamName: 'page[number]',
    pageSizeParamName: 'page[per_page]',
    responseTotalProperty: 'page.total',
    listeners: {
      afterRequest: () => {
        if (intervalRef.current && isPolling) {
          clearInterval(intervalRef.current);
        }
      },
      refresh: ({ source: ajaxStore, action }) => {
        if (action === 'pageLoad' && Array.isArray(ajaxStore.records)) {
          setHasReview(ajaxStore.records.length > 0);
        }
      },
      sort: ({ source: ajaxStore, sorters }) => {
        // Handle sorting
        if (ajaxStore) {
          const { params: currentParams } = ajaxStore;
          const { ascending, field } = sorters[0];
          const params = {
            ...currentParams,
            sort: ascending
              ? FIELDS_TO_COLUMNS[field]
              : `-${FIELDS_TO_COLUMNS[field]}`,
          };
          ajaxStore.setConfig({ params });
          ajaxStore.loadPage(1, params);
        }
      },
    },
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <>
      <ReviewModalContext
        openReviewModal={openReviewModal}
        setOpenReviewModal={setOpenReviewModal}
        gridAction={gridAction}
      >
        {useMemo(
          () => (
            <>
              <div
                className={styles['pulse-bryntum-grid-review']}
                data-testid="pulse-bryntum-grid-review"
                data-empty={isEmptyStateReviews}
              ></div>
              {isEmptyStateReviews && <EmptyReview scope={reviewScope} />}
              {/* Keep the store mounted so filters and reloads work while empty. */}
              <div
                className={clsx(
                  isEmptyStateReviews && styles['review-grid--hidden']
                )}
              >
                <PulseBryntumGrid
                  ref={gridRef}
                  designVariant="v3"
                  gridProps={{
                    columns: columnsConfig(),
                    sortFeature: true,
                    rowHeight: 40,
                    cellMenuFeature: false,
                    cellCls: styles['custom-col-wrapper'],
                    headerMenuFeature: {
                      items: {
                        hideColumn: false,
                        multiSort: false,
                      },
                    },
                    cls: 'pulse-bryntum-grid-review-root',
                    bbar: {
                      dataSummary: {
                        cls: 'review-data-summary',
                      },
                      firstPageButton: {
                        cls: 'first-page-button',
                      },
                      previousPageButton: {
                        cls: 'previous-page-button',
                      },
                      nextPageButton: {
                        cls: 'next-page-button',
                      },
                      lastPageButton: {
                        cls: 'last-page-button',
                      },
                      showLabel: {
                        type: 'displayfield',
                        cls: 'rows-per-page-label',
                        html: 'Show',
                        weight: 151,
                        width: 30,
                      },
                      rowsPerPage: {
                        type: 'combo',
                        cls: 'rows-per-page-combo',
                        width: 64,
                        weight: 152,
                        placeholder: 'Rows per page',
                        items: [
                          { id: 5, text: '5' },
                          { id: 10, text: '10' },
                        ],
                        value: 10,
                        onSelect({ record }) {
                          if (gridRef?.current) {
                            const { gridInstance } = gridRef.current;
                            gridInstance.store.pageSize = record.value;
                            gridInstance.store.loadPage(1);
                          }
                        },
                      },
                      rowsLabel: {
                        type: 'displayfield',
                        cls: 'rows-per-page-label',
                        html: 'rows',
                        weight: 152,
                        width: 26,
                      },
                      pageNumber: {
                        cls: 'page-number',
                      },
                      pageCount: {
                        cls: 'page-count',
                      },
                    },
                    ...gridProps,
                  }}
                  ajaxStoreConfig={AJAX_STORE_CONFIG}
                  paginate={true}
                />
              </div>
            </>
          ),
          [isEmptyStateReviews]
        )}
      </ReviewModalContext>
    </>
  );
};

export const ReviewList = (props) => {
  const { filterParam, limit, emptyReviewScope = 'widget' } = props;
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewDetail, setReviewDetail] = useState(null);
  const gridAction = isEmpty(filterParam)
    ? GRID_ACTIONS.READ
    : GRID_ACTIONS.FILTER;
  const [openReviewModal, setOpenReviewModal] = useState(false);

  const assetDetailInstance = useRef();

  const filterString = qs.stringify(
    { ...PARAMS_DEFAULT, ...filterParam },
    { encode: false }
  );

  const fetchUrl = `${URLS.app}api/round-stages?${filterString}&page[number]=1&page[per_page]=${limit}`;

  useEffect(() => {
    let isCurrentRequest = true;
    setIsLoading(true);
    v2Endpoint
      .get(fetchUrl)
      .then(({ data }) => {
        if (isCurrentRequest) setReviews(data.data);
      })
      .catch(() => {
        if (isCurrentRequest)
          utilities.notification.danger('Fetch reviews failed!');
      })
      .finally(() => {
        if (isCurrentRequest) setIsLoading(false);
      });
    // Ignore this request after fetchUrl changes or the component unmounts.
    return () => {
      isCurrentRequest = false;
    };
  }, [fetchUrl]);

  const reviewDetailCpn = useMemo(
    () =>
      reviewDetail && (
        <Suspense fallback={<div>Loading...</div>}>
          <PulseAssetDetail
            initialState={{
              assetDetail: {
                asset: {
                  assetId: reviewDetail.assetId,
                },
                isZiflow: true,
                isOpenDrawer: true,
                isOpenHistory: false,
                isAdvancedPanelCreated: false,
                isOpenAdvanceDetails: false,
                isOpenWorkFlowTabs: false,
                isOpenAssetDetailsTabs: false,
                isConfirmationModalVisible: false,
              },
              assetComments: {
                comments: [],
                versionComments: [],
                ziflowActivities: [],
              },
              assetVersions: {
                comments: [],
              },
              ticket: {
                isUpdatingTicketProperty: [],
                isUpdatingTicketField: [],
              },
              ticketHistory: {},
              emailHistory: {
                data: [],
              },
              review: {
                reviewRound: null,
                tasks: [],
                isLoadingReviewRound: false,
                setOpenReviewModal,
              },
            }}
            ticketId={0}
            roundId={reviewDetail.roundId}
            assetId={reviewDetail.assetId}
            ref={assetDetailInstance}
            onClose={() => {
              setReviewDetail(null);
            }}
          />
        </Suspense>
      ),
    [reviewDetail?.assetId, reviewDetail?.roundId]
  );

  const renderReviews = () => {
    if (isLoading) {
      return (
        <div className={styles['loading-reviews']}>
          <PulseXhrRequestStatus description={<span>Loading</span>} />
        </div>
      );
    }

    if (reviews.length === 0) {
      return <EmptyReview scope={emptyReviewScope} />;
    }

    return (
      <div>
        {reviews.map((review) => {
          const {
            created_at,
            name,
            round_sources,
            round_title,
            round_thumbnail_link,
            job_number,
            job_id,
            job_title,
            round_decision_status,
            round_status_label,
            progress,
            status,
            reviewers,
            round_id,
          } = review?.attributes;

          const displayedReviewers = reviewers.slice(0, 3);
          const { asset } = round_sources[0];
          const src = `${URLS.pulse}openpreview.php?format=thumb&assetid=`;
          let assetThumbnail;
          if (asset) {
            assetThumbnail = (
              <img
                className={styles['custom-review-img']}
                src={asset?.has_preview ? `${src}${asset?.id}` : defaultV3Image}
              />
            );
          } else {
            assetThumbnail = round_thumbnail_link ? (
              <i className={'fa-light fa-browser'} />
            ) : (
              <img
                className={styles['custom-review-img']}
                src={defaultV3Image}
              />
            );
          }
          return (
            <div
              className={styles['review-item']}
              key={review.id}
              onClick={() => {
                setReviewDetail({ roundId: round_id, assetId: asset?.id });
              }}
            >
              <div className={styles['review-item__image']}>
                {assetThumbnail}
              </div>
              <div className={styles['review-item__content']}>
                <div className={styles['review-item__info']}>
                  <div className={styles['review-item__title']}>
                    {round_title}
                  </div>
                  {job_id && (
                    <div className={styles['review-item__job']}>
                      <span className={styles['job-number']}>
                        <i className="fa-solid fa-rocket-launch"></i>
                        <span>{job_number}</span>
                      </span>
                      <a
                        className={styles['job-title']}
                        href={`${URLS.app}passport/${job_id}`}
                      >
                        {job_title}
                      </a>
                    </div>
                  )}
                  <div className={styles['review-item__reviewers']}>
                    <div className={styles['reviewer-avatars']}>
                      {displayedReviewers.map((reviewer) => {
                        return (
                          <img
                            key={reviewer?.id}
                            className={styles['reviewer-avatar']}
                            src={reviewer?.avatar_url}
                            alt={reviewer?.name}
                          />
                        );
                      })}
                      {reviewers.length - 3 > 0 && (
                        <div className={styles['extra-count']}>
                          +{reviewers.length - 3}
                        </div>
                      )}
                    </div>
                    <PulseStatusProgress
                      progress={
                        status !== STAGE_STATUS.notStarted
                          ? progress
                          : DEFAULT_PROGRESS
                      }
                      designVariant="v3"
                      view="review"
                      classes={{
                        root: styles['review-status'],
                      }}
                    />
                  </div>
                  <div className={styles['review-item__footer']}>
                    {name} |{' '}
                    {format(new Date(created_at), dateFormatBasedOnCountry())}
                  </div>
                </div>
                <div
                  className={clsx(
                    styles['review-item__status'],
                    styles[`${round_decision_status}`]
                  )}
                >
                  {round_status_label}
                </div>
              </div>
            </div>
          );
        })}
        {reviewDetailCpn}
      </div>
    );
  };

  return (
    <ReviewModalContext
      openReviewModal={openReviewModal}
      setOpenReviewModal={setOpenReviewModal}
      gridAction={gridAction}
    >
      {renderReviews()}
    </ReviewModalContext>
  );
};

DocumentReviewGrid.propTypes = {
  ajaxStore: PropTypes.object,
  gridProps: PropTypes.object,
  paramFilter: PropTypes.object,
  leftNavParam: PropTypes.object,
  filterFlyoutParam: PropTypes.object,
  searchValue: PropTypes.string,
  limit: PropTypes.number,
  isTaskForm: PropTypes.bool,
  ticketId: PropTypes.number,
  rejectFilterJobId: PropTypes.bool,
  emptyReviewScope: PropTypes.oneOf(['reviews', 'project', 'task', 'widget']),
};

ReviewList.propTypes = {
  filterParam: PropTypes.object,
  limit: PropTypes.number,
  emptyReviewScope: PropTypes.oneOf(['reviews', 'project', 'task', 'widget']),
};

export default React.memo(DocumentReviewGrid);
